import { supabase } from '@/integrations/supabase/client';

export interface AssignSlotResult {
  success: boolean;
  newVersion?: number;
  slotIndex?: number;
  conflict?: boolean;
  currentVersion?: number;
  error?: string;
}

export async function assignPostToSlot(
  planId: string,
  slotId: string,
  postId: string,
  expectedVersion: number,
): Promise<AssignSlotResult> {
  const { data, error } = await supabase.rpc('assign_post_to_slot', {
    p_plan_id: planId,
    p_slot_id: slotId,
    p_post_id: postId,
    p_expected_version: expectedVersion,
  } as never);

  if (error) return { success: false, error: error.message };
  if (!data || typeof data !== 'object') return { success: false, error: 'invalid_response' };

  const d = data as Record<string, unknown>;
  if (d.success) {
    return {
      success: true,
      newVersion: d.new_version as number,
      slotIndex: d.position as number,
    };
  }
  if (d.conflict) {
    return {
      success: false,
      conflict: true,
      currentVersion: d.current_version as number,
    };
  }
  return { success: false, error: (d.error as string) ?? 'unknown' };
}

/**
 * Read a plan's current optimistic-concurrency version, needed before calling
 * `assignPostToSlot` (compare-and-swap). Lives here (lib) so pages/components
 * never import the Supabase client directly. Returns 0 when the plan is missing
 * or has no version yet.
 */
export async function getPlanVersion(planId: string): Promise<number> {
  const { data } = await supabase
    .from('projects')
    .select('version')
    .eq('id', planId)
    .maybeSingle();
  return (data as { version?: number } | null)?.version ?? 0;
}
